import React, { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, AlertCircle, Loader, Mail, Inbox } from 'lucide-react'
import { apiClient } from '@/services/api'
import styles from './AuthStandalone.module.css'

type Status = 'waiting' | 'loading' | 'success' | 'error'

export function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email')
  const [status, setStatus] = useState<Status>(
    token ? 'loading' : email ? 'waiting' : 'error'
  )
  const [message, setMessage] = useState('')
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)

  useEffect(() => {
    if (!token) {
      if (!email) setMessage('No verification token provided.')
      return
    }

    apiClient.verifyEmail(token)
      .then(() => {
        setStatus('success')
        setMessage('Your email has been verified successfully!')
      })
      .catch((err) => {
        setStatus('error')
        setMessage(err.response?.data?.error || 'Invalid or expired verification token.')
      })
  }, [token, email])

  const handleResend = async () => {
    setResending(true)
    try {
      await apiClient.resendVerification(email || undefined)
      setResent(true)
      setMessage('A new verification email has been sent. Please check your inbox.')
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Failed to resend verification email. Please log in first.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.cardPrimary}>
        <h1 className={styles.titleLarge}>Email Verification</h1>

        {status === 'waiting' && (
          <div className={styles.statusColumn}>
            <Inbox className={`w-12 h-12 ${styles.secondaryIcon}`} />
            <div>
              <p className={styles.statusTitle}>Check your email!</p>
              <p className={styles.statusMessage}>
                We sent a verification link to{' '}
                <strong className={styles.highlight}>{email}</strong>.
                <br />
                Click the link to verify your account.
              </p>
            </div>
            <div className={styles.actionColumn}>
              <Link to="/auth" className={styles.linkLarge}>
                Back to Sign In
              </Link>
              {!resent && (
                <p className={styles.resendNote}>
                  Didn't receive it?{' '}
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className={styles.resendBtn}
                  >
                    {resending ? 'Sending...' : 'Resend email'}
                  </button>
                </p>
              )}
              {resent && (
                <p className={styles.resentNote}>
                  <CheckCircle className={`w-3.5 h-3.5 ${styles.resentIcon}`} />
                  Verification email resent!
                </p>
              )}
            </div>
          </div>
        )}

        {status === 'loading' && (
          <div className={styles.statusColumnSmall}>
            <Loader className={`w-10 h-10 animate-spin ${styles.secondaryIcon}`} />
            <p className={styles.statusText}>Verifying your email...</p>
          </div>
        )}

        {status === 'success' && (
          <div className={styles.statusColumn}>
            <CheckCircle className={`w-12 h-12 ${styles.primaryIcon}`} />
            <p className={styles.successMessage}>{message}</p>
            <Link to="/auth" className={styles.link}>
              Sign In
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className={styles.statusColumn}>
            <AlertCircle className={`w-12 h-12 ${styles.dangerIconInline}`} />
            <p className={styles.errorMessage}>{message}</p>
            <div className={styles.errorActions}>
              <button
                onClick={handleResend}
                disabled={resending}
                className={styles.resendButton}
              >
                {resending ? <Loader className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Resend Email
              </button>
              <Link to="/auth" className={styles.errorLink}>
                Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
